<template>
  <div class="home">
    <section id="home-top" ref="hero" class="home-hero" :class="{ 'is-paused': !heroState.running }">
      <!-- An illustrated, living scene instead of a stock photo. Each layer is
           its own component (see components/Home/hero); they share the hero's
           size, pause and parallax state through the provided heroState and
           the --hero-* custom properties set on this section. -->
      <div class="hero-scene" aria-hidden="true">
        <HeroSky/>
        <HeroBirds/>
        <HeroLandscape/>
        <HeroRabbit/>
        <HeroGrassFringe/>
      </div>

      <div id="top-box" class="hero-content">
        <div class="container hero-inner text-center">
          <!-- Both theme variants of the logo and the title are stacked and
               crossfaded, so they follow the sky's day/night transition
               instead of flipping instantly. -->
          <div class="hero-logo-wrap">
            <img class="hero-logo hero-logo--on-dark" src="@/assets/logo.png" alt="Zerticarbon">
            <img class="hero-logo hero-logo--on-light" src="@/assets/img/zerticarbon_green_logo.png" alt="" aria-hidden="true">
          </div>
          <h1 class="hero-title">
            <span class="hero-title-text hero-title-text--night">ZERTI CARBON</span>
            <span class="hero-title-text hero-title-text--day" aria-hidden="true">ZERTI CARBON</span>
          </h1>
          <h2 class="hero-subtitle">BLOCKCHAIN CERTIFIED CARBON SINK SOLUTIONS</h2>
          <div class="hero-actions">
            <button class="btn btn-primary btn-lg hero-cta" @click="$router.push('/areas-map')">
              <i class="fas fa-globe-europe mr-2"></i>Explore the forest map
            </button>
            <button class="btn btn-lg hero-cta hero-cta--ghost" @click="$router.push('/login')">
              <i class="fas fa-sign-in-alt mr-2"></i>Sign in
            </button>
          </div>

          <div class="stats-grid">
            <div v-for="(card, i) in statCards" :key="card.key" class="stat-card"
                 :style="{ animationDelay: (0.35 + i * 0.08) + 's' }">
              <div class="stat-card-icon"><i :class="card.icon"></i></div>
              <div class="stat-card-value">
                <template v-if="stats">{{ card.formatted }}</template>
                <LazyLoadingPlaceholder v-else/>
              </div>
              <div class="stat-card-label">{{ card.label }}</div>
            </div>
          </div>

          <h5 class="hero-tagline">
            <span>Forestation</span><span class="hero-tagline-dot">&middot;</span><span>Digitalization</span><span class="hero-tagline-dot">&middot;</span><span>Monetization</span>
          </h5>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Stats from "../api/Stats";
import LazyLoadingPlaceholder from "@/components/Home/LazyLoadingPlaceholder.vue";
import HeroSky from "@/components/Home/hero/HeroSky.vue";
import HeroBirds from "@/components/Home/hero/HeroBirds.vue";
import HeroLandscape from "@/components/Home/hero/HeroLandscape.vue";
import HeroRabbit from "@/components/Home/hero/HeroRabbit.vue";
import HeroGrassFringe from "@/components/Home/hero/HeroGrassFringe.vue";
import { HERO_PARALLAX_SELECTOR, registerHeroParallax } from "@/components/Home/hero/heroMotion";

export default {
  name: 'Home',
  components: {
    LazyLoadingPlaceholder,
    HeroSky,
    HeroBirds,
    HeroLandscape,
    HeroRabbit,
    HeroGrassFringe
  },
  provide() {
    return { heroState: this.heroState };
  },
  data() {
    return {
      stats: null,
      animated: {
        areasQty: 0,
        totalArea: 0,
        totalTrees: 0,
        totalTheoreticalYearlyIncrement: 0
      },
      // Shared with every scene component (see hero/heroMotion.js).
      heroState: {
        running: true,
        reducedMotion: false,
        width: 0,
        height: 0
      }
    }
  },
  computed: {
    statCards() {
      const fmt0 = (n) => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(n || 0);
      const fmt2 = (n) => new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(n || 0);

      return [
        { key: 'areas', icon: 'fas fa-tree', label: 'Forests', formatted: fmt0(this.animated.areasQty) },
        { key: 'area', icon: 'fas fa-ruler-combined', label: 'Total Farmed Ha', formatted: fmt2(this.animated.totalArea) + ' Ha' },
        { key: 'trees', icon: 'fas fa-seedling', label: 'Total Trees', formatted: fmt0(this.animated.totalTrees) },
        { key: 'co2', icon: 'fas fa-cloud', label: 'CCO2 Tons Farmed / Year', formatted: fmt2(this.animated.totalTheoreticalYearlyIncrement) }
      ];
    }
  },
  created() {
    // A first estimate so the scene can lay itself out before the
    // ResizeObserver reports the hero's real size.
    this.heroState.width = document.documentElement.clientWidth;
    this.heroState.height = Math.max(window.innerHeight - 100, 460);
    this.heroVisible = true;
    this.pageVisible = !document.hidden;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.heroState.reducedMotion = this.motionQuery.matches;
    this.updateRunning();

    Stats.getStats()
      .then((stats) => {
        this.stats = stats.data;
        this.animateStats(stats.data);
      }).catch((error) => {
      console.log(error)
    })
  },
  mounted() {
    const hero = this.$refs.hero;

    this.onMotionChange = () => {
      this.heroState.reducedMotion = this.motionQuery.matches;
      this.updateRunning();
      this.resetParallax();
    };
    if (this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener('change', this.onMotionChange);
    } else {
      this.motionQuery.addListener(this.onMotionChange);
    }

    this.onVisibilityChange = () => {
      this.pageVisible = !document.hidden;
      this.updateRunning();
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if ('IntersectionObserver' in window) {
      this.intersection = new IntersectionObserver((entries) => {
        this.heroVisible = entries[entries.length - 1].isIntersecting;
        this.updateRunning();
      });
      this.intersection.observe(hero);
    }

    // The layout size, not getBoundingClientRect(): that one includes
    // ancestor transforms (such as a route transition's scale), and since the
    // layout box itself never changes, no later resize would correct it.
    const measure = () => {
      this.heroState.width = hero.offsetWidth;
      this.heroState.height = hero.offsetHeight;
    };
    measure();
    if ('ResizeObserver' in window) {
      this.resizer = new ResizeObserver(measure);
      this.resizer.observe(hero);
    } else {
      this.onWindowResize = measure;
      window.addEventListener('resize', measure);
    }

    // A gentle pointer parallax — only where there is a real, hovering
    // pointer (it would just jitter on touch screens).
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      registerHeroParallax();
      this.parallax = { x: 0, y: 0, tx: 0, ty: 0, raf: 0, targets: [] };
      this.onPointerMove = (event) => {
        if (this.heroState.reducedMotion || !this.heroState.running) return;
        const rect = hero.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        this.parallax.tx = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width) * 2 - 1));
        this.parallax.ty = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height) * 2 - 1));
        if (!this.parallax.raf) {
          // Looked up once per burst of movement, so wrappers a component
          // re-rendered since the last one are picked up.
          this.collectParallaxTargets();
          this.parallax.raf = requestAnimationFrame(this.stepParallax);
        }
      };
      window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    }
  },
  beforeDestroy() {
    if (this.motionQuery && this.onMotionChange) {
      if (this.motionQuery.removeEventListener) {
        this.motionQuery.removeEventListener('change', this.onMotionChange);
      } else {
        this.motionQuery.removeListener(this.onMotionChange);
      }
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.intersection) this.intersection.disconnect();
    if (this.resizer) this.resizer.disconnect();
    if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize);
    if (this.onPointerMove) window.removeEventListener('pointermove', this.onPointerMove);
    if (this.parallax) cancelAnimationFrame(this.parallax.raf);
  },
  methods: {
    updateRunning() {
      this.heroState.running = this.heroVisible && this.pageVisible && !this.heroState.reducedMotion;
    },
    // The scene's parallax wrappers (see HERO_PARALLAX_SELECTOR).
    collectParallaxTargets() {
      const hero = this.$refs.hero;
      this.parallax.targets = hero ? Array.prototype.slice.call(hero.querySelectorAll(HERO_PARALLAX_SELECTOR)) : [];
    },
    // Writes --hero-px / --hero-py on the section (HeroBirds reads them from
    // there) and on each parallax wrapper. The properties are registered as
    // non-inherited, so this restyles only those few elements instead of the
    // thousands of SVG nodes below them.
    writeParallax(x, y) {
      const hero = this.$refs.hero;
      if (!hero) return;
      const px = x.toFixed(4);
      const py = y.toFixed(4);
      hero.style.setProperty('--hero-px', px);
      hero.style.setProperty('--hero-py', py);
      const targets = this.parallax.targets;
      for (let i = 0; i < targets.length; i++) {
        targets[i].style.setProperty('--hero-px', px);
        targets[i].style.setProperty('--hero-py', py);
      }
    },
    // Eases the scene's --hero-px / --hero-py (-1..1) toward the pointer and
    // stops requesting frames as soon as it has caught up.
    stepParallax() {
      const p = this.parallax;
      p.x += (p.tx - p.x) * 0.06;
      p.y += (p.ty - p.y) * 0.06;
      if (p.targets.some((el) => !el.isConnected)) this.collectParallaxTargets();
      this.writeParallax(p.x, p.y);
      const settled = Math.abs(p.tx - p.x) < 0.001 && Math.abs(p.ty - p.y) < 0.001;
      p.raf = settled ? 0 : requestAnimationFrame(this.stepParallax);
    },
    resetParallax() {
      if (!this.parallax || !this.heroState.reducedMotion) return;
      cancelAnimationFrame(this.parallax.raf);
      this.parallax.raf = 0;
      this.parallax.tx = this.parallax.ty = 0;
      this.parallax.x = this.parallax.y = 0;
      this.collectParallaxTargets();
      this.writeParallax(0, 0);
    },
    animateStats(target) {
      const keys = ['areasQty', 'totalArea', 'totalTrees', 'totalTheoreticalYearlyIncrement'];
      const from = { ...this.animated };
      const duration = 1400;
      const start = performance.now();

      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);

        keys.forEach((key) => {
          const targetValue = parseFloat(target[key]) || 0;
          this.animated[key] = from[key] + (targetValue - from[key]) * eased;
        });

        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };

      requestAnimationFrame(step);
    }
  }
}
</script>

<style scoped lang="scss">
/* ---------------------------------------------------------------------- */
/* Scene contract (shared with components/Home/hero/*)                    */
/* ---------------------------------------------------------------------- */

// The app-wide page fade-in would slide the whole scene up on top of its own
// choreographed entrance, and hide the sky on the first frames. That covers
// both the global fadeInUp keyframes and App.vue's route transition, whose
// enter state would also scale the scene to 99% while the sun, the moon and
// the mountains are rising.
.home {
  animation: none;
}

.home.page-fade-enter {
  opacity: 1;
  transform: none;
}

.home-hero {
  isolation: isolate;
  // Height of the whole landscape band (mountains + forest + meadow),
  // anchored to the bottom of the hero. It follows the screen height too, so
  // tall portrait screens show a real landscape instead of a thin strip; the
  // cap only grows past 330px on screens over ~1030px tall (2560x1440 and
  // up), where a 330px strip would read as a thin band under empty sky.
  --hero-land-h: clamp(150px, max(17vw, 24vh), max(330px, min(440px, 32vh)));
  // Height of the near meadow strip at the very bottom, where the rabbit
  // hops; its top edge is the meadow's horizon.
  --hero-ground-h: clamp(28px, 3.4vw, 46px);
  // Pointer parallax, -1..1, eased by Home.vue (0 on touch / reduced motion).
  // Registered as non-inherited (heroMotion.js), so Home.vue writes it on the
  // parallax wrappers themselves.
  --hero-px: 0;
  --hero-py: 0;
}

// Longhands rather than `inset`, which older mobile engines ignore — the
// scene would collapse to 0x0 and clip every layer away.
.hero-scene {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

// Back-to-front stacking of the scene layers (their own components position
// themselves; this only fixes the order).
.hero-scene > .hero-sky { z-index: 1; }
.hero-scene > .hero-birds { z-index: 2; }
.hero-scene > .hero-landscape { z-index: 3; }
.hero-scene > .hero-rabbit { z-index: 4; }
.hero-scene > .hero-grass-fringe { z-index: 5; }

// Off-screen, hidden tab or reduced motion: freeze every CSS animation in
// the scene where it is (JS loops stop through heroState.running).
.home-hero.is-paused .hero-scene ::v-deep * {
  animation-play-state: paused !important;
}

/* ---------------------------------------------------------------------- */
/* Hero content                                                           */
/* ---------------------------------------------------------------------- */

#home-top #top-box.hero-content {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: inherit;
}

.hero-inner {
  // Clear of the header on top, and of the forest at the bottom: the text
  // block is centred in the sky above most of the landscape band.
  padding-top: clamp(20px, 4vh, 40px);
  padding-bottom: calc(var(--hero-land-h) * 0.72);
  max-width: 980px;
}

@media (max-width: 575px) {
  .hero-inner {
    padding-bottom: calc(var(--hero-land-h) * 0.8);
  }
}

// The hero's text follows the sky when the theme changes. The sky passes
// the midpoint of its change ~0.15 s after the switch, in both directions,
// so the text flips around then too, quickly, instead of fading slowly
// through a washed-out grey against a sky that has already changed.
//
// Stacked day/night copies (logo, title): the incoming copy is raised on top
// and fades in fast, while the outgoing one stays fully opaque underneath a
// little longer — the sky never shows through a half-transparent title.
$theme-swap-in: opacity 0.3s cubic-bezier(0.22, 1, 0.36, 1);
$theme-swap-out: opacity 0.35s cubic-bezier(0.55, 0, 1, 0.45) 0.25s;
// Single-copy text colours: a short switch centred on ~0.16 s.
$theme-ink: 0.2s cubic-bezier(0.45, 0, 0.55, 1) 0.06s;
// Glass surfaces behind text (stat cards, tagline): cross their midpoint at
// about the same moment as the text on them.
$theme-surface: 0.4s cubic-bezier(0.45, 0, 0.25, 1);

.hero-logo-wrap {
  display: grid;
  justify-items: center;
  margin-bottom: clamp(10px, 2vh, 18px);
  animation: fadeInUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.hero-logo {
  grid-area: 1 / 1;
  width: clamp(130px, 16vw, 176px);
  max-width: 100%;
  height: auto;
}

.hero-logo--on-dark {
  z-index: 1;
  filter: drop-shadow(0 4px 18px rgba(0, 0, 0, 0.35));
  transition: $theme-swap-in;
}

.hero-logo--on-light {
  z-index: 0;
  opacity: 0;
  filter: drop-shadow(0 2px 10px rgba(255, 255, 255, 0.7));
  transition: $theme-swap-out;
}

[data-theme="light"] .hero-logo--on-dark {
  z-index: 0;
  opacity: 0;
  transition: $theme-swap-out;
}

[data-theme="light"] .hero-logo--on-light {
  z-index: 1;
  opacity: 1;
  transition: $theme-swap-in;
}

#home-top .hero-title {
  display: grid;
  justify-items: center;
  margin: 0 0 6px;
  font-size: clamp(2.1rem, 5.6vw, 3.6rem);
  font-weight: 800;
  line-height: 1.02;
  letter-spacing: 0.01em;
  text-shadow: none;
  animation: fadeInUp 0.7s 0.08s cubic-bezier(0.22, 1, 0.36, 1) both;
}

// Gradient text can't transition its gradient, so a day and a night copy are
// stacked in the same cell and crossfaded (see $theme-swap-in/out).
.hero-title-text {
  grid-area: 1 / 1;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-title-text--night {
  z-index: 1;
  background-image: linear-gradient(180deg, #ffffff 30%, #cfe9d2 100%);
  filter: drop-shadow(0 3px 16px rgba(0, 0, 0, 0.45));
  transition: $theme-swap-in;
}

.hero-title-text--day {
  z-index: 0;
  opacity: 0;
  background-image: linear-gradient(180deg, #1c3a24 20%, #3d7a3f 100%);
  filter: drop-shadow(0 2px 14px rgba(255, 255, 255, 0.8));
  transition: $theme-swap-out;
}

[data-theme="light"] .hero-title-text--night {
  z-index: 0;
  opacity: 0;
  transition: $theme-swap-out;
}

[data-theme="light"] .hero-title-text--day {
  z-index: 1;
  opacity: 1;
  transition: $theme-swap-in;
}

#home-top .hero-subtitle {
  margin: 0 auto clamp(16px, 2.6vh, 24px);
  max-width: 34em;
  font-size: clamp(0.92rem, 2.1vw, 1.32rem);
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: 0.06em;
  color: rgba(236, 246, 238, 0.92);
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0), 0 2px 14px rgba(0, 0, 0, 0.5);
  transition: color $theme-ink, text-shadow $theme-ink;
  animation: fadeInUp 0.7s 0.16s cubic-bezier(0.22, 1, 0.36, 1) both;
}

[data-theme="light"] #home-top .hero-subtitle {
  color: #2c4a33;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.6), 0 2px 16px rgba(255, 255, 255, 0.85);
}

.hero-cta {
  position: relative;
  overflow: hidden;
  margin-bottom: clamp(20px, 3.4vh, 32px);
  padding: 0.62rem 1.5rem;
  border-radius: 999px;
  font-weight: 700;
  animation: fadeInUp 0.7s 0.24s cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease;

  // A soft highlight that sweeps across on hover.
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -60%;
    width: 50%;
    height: 100%;
    background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.35), transparent);
    transform: skewX(-18deg);
    transition: left 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &:hover {
    transform: translateY(-2px);
  }

  &:hover::after {
    left: 120%;
  }
}

// the two calls to action side by side; stacked full width on narrow phones
.hero-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: clamp(20px, 3.4vh, 32px);

  .hero-cta + .hero-cta { animation-delay: 0.34s; }
}

// (with the id: the compact-screen rules below give #home-top .hero-cta its own margin)
#home-top .hero-actions .hero-cta { margin-bottom: 0; }

.hero-cta--ghost {
  color: #fff;
  background: rgba(8, 24, 15, 0.35);
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(8px);

  &:hover, &:focus { color: #fff; background: rgba(8, 24, 15, 0.55); }
}

[data-theme="light"] .hero-cta--ghost {
  color: #1f4d2c;
  background: rgba(255, 255, 255, 0.55);
  border-color: rgba(31, 77, 44, 0.35);

  &:hover, &:focus { color: #1f4d2c; background: rgba(255, 255, 255, 0.8); }
}

@media (max-width: 480px) {
  .hero-actions { flex-direction: column; align-items: stretch; width: min(320px, 100%); margin-left: auto; margin-right: auto; }
}

.hero-tagline {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 4px 10px;
  margin: clamp(18px, 3vh, 28px) 0 0;
  padding: 8px 18px;
  border-radius: 999px;
  font-size: clamp(0.78rem, 1.5vw, 0.95rem);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background-color: rgba(8, 20, 12, 0.32);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 8px 24px -14px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: color $theme-ink, background-color $theme-surface, border-color $theme-surface, box-shadow $theme-surface;
  animation: fadeInUp 0.7s 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
}

#home-top .hero-tagline {
  color: rgba(240, 248, 241, 0.95);
  text-shadow: none;
}

.hero-tagline-dot {
  color: #9ed77f;
  transition: color $theme-ink;
}

[data-theme="light"] .hero-tagline {
  background-color: rgba(255, 255, 255, 0.6);
  border-color: rgba(51, 99, 58, 0.16);
  box-shadow: 0 8px 24px -14px rgba(23, 40, 26, 0.35);
}

[data-theme="light"] #home-top .hero-tagline {
  color: #24412b;
}

[data-theme="light"] .hero-tagline-dot {
  color: #4f8c4a;
}

// Tight enough to stay on one line on regular phones; on the narrowest ones
// the three words wrap as a group, so the separators are dropped instead of
// being left dangling at a line end.
@media (max-width: 420px) {
  .hero-tagline {
    gap: 4px 6px;
    padding: 7px 12px;
    font-size: 0.66rem;
    letter-spacing: 0.03em;
  }
}

@media (max-width: 359px) {
  .hero-tagline {
    gap: 2px 10px;
    border-radius: 16px;
  }

  .hero-tagline-dot {
    display: none;
  }
}

/* ---------------------------------------------------------------------- */
/* Stats                                                                  */
/* ---------------------------------------------------------------------- */

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  max-width: 900px;
  margin: 0 auto;
}

@media (max-width: 767px) {
  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
}

.stat-card {
  position: relative;
  overflow: hidden;
  padding: 16px 12px 14px;
  border-radius: 18px;
  background-color: rgba(8, 22, 13, 0.4);
  background-image: linear-gradient(160deg, rgba(255, 255, 255, 0.13) 0%, rgba(255, 255, 255, 0.04) 100%);
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow: 0 18px 40px -22px rgba(0, 0, 0, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(14px) saturate(150%);
  -webkit-backdrop-filter: blur(14px) saturate(150%);
  color: #fff;
  animation: fadeInUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.45s ease, border-color 0.45s ease, background-color $theme-surface, color $theme-ink;

  // A thin accent line along the top edge that lights up on hover.
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 18%;
    right: 18%;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(90deg, transparent, var(--accent-green-light), transparent);
    opacity: 0.35;
    transition: opacity 0.3s ease, left 0.3s ease, right 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(158, 215, 127, 0.4);
    box-shadow: 0 24px 46px -20px rgba(0, 0, 0, 0.75), 0 0 0 1px rgba(158, 215, 127, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }

  &:hover::before {
    opacity: 1;
    left: 8%;
    right: 8%;
  }
}

[data-theme="light"] .stat-card {
  background-color: rgba(255, 255, 255, 0.66);
  background-image: linear-gradient(160deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.04) 100%);
  border-color: rgba(51, 99, 58, 0.14);
  box-shadow: 0 18px 40px -24px rgba(23, 40, 26, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.9);
  color: #173a22;

  &:hover {
    border-color: rgba(61, 122, 63, 0.35);
    box-shadow: 0 24px 46px -22px rgba(23, 40, 26, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  &::before {
    background: linear-gradient(90deg, transparent, var(--accent-green), transparent);
  }
}

.stat-card-icon {
  width: 42px;
  height: 42px;
  margin: 0 auto 10px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-gradient);
  box-shadow: 0 6px 18px -4px var(--accent-glow), inset 0 1px 0 rgba(255, 255, 255, 0.25);
  font-size: 1.05rem;
  color: #fff;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.stat-card:hover .stat-card-icon {
  transform: scale(1.08) rotate(-6deg);
}

.stat-card-value {
  font-family: 'Sora', 'Plus Jakarta Sans', sans-serif;
  font-size: clamp(1.15rem, 2.2vw, 1.55rem);
  font-weight: 800;
  line-height: 1.15;
  min-height: 1.4em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-shadow: 0 1px 10px rgba(0, 0, 0, 0.3);
  transition: text-shadow $theme-ink;
}

[data-theme="light"] .stat-card-value {
  text-shadow: 0 1px 10px rgba(0, 0, 0, 0);
}

.stat-card-label {
  margin-top: 4px;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.78;
}

@media (max-width: 575px) {
  .stat-card {
    padding: 13px 8px 12px;
    border-radius: 16px;
  }

  .stat-card-icon {
    width: 36px;
    height: 36px;
    margin-bottom: 8px;
    font-size: 0.92rem;
  }

  // Never below ~10.5px, however small the root font is.
  .stat-card-label {
    font-size: max(0.62rem, 10.5px);
  }
}

// The narrowest phones: the values follow the card width, so a long one
// ("5.502,29 Ha") keeps clear of the card's clipped edge.
@media (max-width: 359px) {
  .stat-card-value {
    font-size: clamp(1rem, 5.2vw, 1.15rem);
  }
}

/* ---------------------------------------------------------------------- */
/* Short screens                                                          */
/* ---------------------------------------------------------------------- */

// Laptop-height screens: a tighter text stack, so the stat cards stop
// covering the mountain peaks and more of the landscape shows.
@media (min-width: 768px) and (max-height: 800px) {
  .hero-inner {
    padding-top: 14px;
  }

  .hero-logo-wrap {
    margin-bottom: 8px;
  }

  .hero-logo {
    width: clamp(110px, 11vw, 140px);
  }

  #home-top .hero-title {
    font-size: clamp(1.9rem, 4.2vw, 2.9rem);
  }

  #home-top .hero-subtitle {
    margin-bottom: 14px;
    font-size: clamp(0.88rem, 1.6vw, 1.12rem);
  }

  .hero-cta {
    margin-bottom: 16px;
    padding: 0.5rem 1.3rem;
    font-size: 1.05rem;
  }

  .stat-card {
    padding: 11px 10px 10px;
  }

  .stat-card-icon {
    width: 34px;
    height: 34px;
    margin-bottom: 6px;
    font-size: 0.88rem;
  }

  .stat-card-value {
    font-size: clamp(1.05rem, 1.9vw, 1.35rem);
  }

  .hero-tagline {
    margin-top: 14px;
    padding: 6px 16px;
  }
}

// Short portrait phones (iPhone SE, 360x640 Androids): the regular phone
// stack pushes the whole landscape below the fold, so the mountains rising,
// the forest growing and the rabbit's burrow entrance would all play
// off-screen. A compact stack keeps them in the first view. The logo stays
// (smaller): without it the title would move up into the moon.
@media (max-width: 575px) and (max-height: 740px) and (orientation: portrait) {
  #home-top .hero-inner {
    padding-top: 12px;
    padding-bottom: calc(var(--hero-land-h) * 0.62);
  }

  #home-top .hero-logo-wrap {
    margin-bottom: 6px;
  }

  #home-top .hero-logo {
    width: 104px;
  }

  #home-top .hero-title {
    font-size: 1.9rem;
  }

  #home-top .hero-subtitle {
    margin-bottom: 12px;
    font-size: 0.82rem;
  }

  #home-top .hero-cta {
    margin-bottom: 14px;
    padding: 0.45rem 1.2rem;
    font-size: 1rem;
  }

  #home-top .stats-grid {
    gap: 8px;
  }

  #home-top .stat-card {
    padding: 9px 6px 8px;
  }

  #home-top .stat-card-icon {
    display: none;
  }

  #home-top .hero-tagline {
    display: none;
  }
}

// Phones held sideways: the screen is barely taller than the header, so the
// hero fits the remaining height (instead of its usual 460px floor) with a
// compact layout, keeping the landscape — the entrance, the forest and the
// rabbit — inside the first view. The subtitle and the button share a row
// and the stack sits at the top of the sky rather than centred in it, which
// lifts the stat cards clear of the far mountain ridge.
@media (max-height: 520px) and (orientation: landscape) {
  .home-hero {
    --hero-land-h: clamp(104px, 32vh, 150px);
    --hero-ground-h: clamp(22px, 6vh, 30px);
  }

  #home-top.home-hero {
    min-height: 0;
  }

  #home-top #top-box.hero-content {
    align-items: flex-start;
  }

  .hero-inner {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    column-gap: 14px;
    padding-top: clamp(10px, 4vh, 20px);
    padding-bottom: calc(var(--hero-land-h) * 0.5);
  }

  .hero-logo-wrap {
    display: none;
  }

  #home-top .hero-title {
    flex: 0 0 100%;
    font-size: 1.7rem;
    margin-bottom: 4px;
  }

  // A small basis keeps the button on the same row on the narrowest phones
  // (the subtitle wraps onto two lines there); elsewhere it grows to its
  // one-line width and no further, so the pair stays centred together.
  #home-top .hero-subtitle {
    flex: 1 1 18em;
    max-width: max-content;
    margin: 0 0 10px;
    font-size: 0.78rem;
  }

  .hero-cta {
    flex: none;
    margin-bottom: 10px;
    padding: 0.36rem 1.1rem;
    font-size: 0.92rem;
  }

  .stats-grid {
    flex: 0 0 100%;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
  }

  .stat-card {
    padding: 7px 6px;
    border-radius: 12px;
  }

  .stat-card-icon {
    display: none;
  }

  .stat-card-value {
    font-size: 0.98rem;
  }

  .stat-card-label {
    margin-top: 1px;
    font-size: 0.62rem;
  }

  .hero-tagline {
    display: none;
  }
}

/* ---------------------------------------------------------------------- */
/* Large screens                                                          */
/* ---------------------------------------------------------------------- */

// 2560x1440 and up: the text stack grows with the screen (each value equals
// its regular size at 1920px wide), so it keeps the same composition with
// the larger landscape instead of an island of small text in empty sky.
@media (min-width: 1920px) and (min-height: 1000px) {
  #home-top .hero-inner {
    max-width: clamp(980px, calc(380px + 31.25vw), 1180px);
  }

  #home-top .hero-logo {
    width: clamp(176px, calc(74px + 5.3vw), 210px);
  }

  #home-top .hero-title {
    font-size: clamp(3.6rem, calc(1.2rem + 2vw), 4.4rem);
  }

  #home-top .hero-subtitle {
    font-size: clamp(1.32rem, calc(0.78rem + 0.45vw), 1.5rem);
  }

  #home-top .stats-grid {
    max-width: clamp(900px, calc(360px + 28.125vw), 1080px);
    gap: clamp(14px, calc(2px + 0.625vw), 18px);
  }

  #home-top .stat-card-value {
    font-size: clamp(1.55rem, calc(0.65rem + 0.75vw), 1.85rem);
  }

  #home-top .stat-card-label {
    font-size: clamp(0.68rem, calc(0.38rem + 0.25vw), 0.78rem);
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-logo-wrap, .hero-title, .hero-subtitle, .hero-cta, .hero-tagline, .stat-card {
    animation: none;
  }
}
</style>
